import { Hub, HubCallback } from '@aws-amplify/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { GraphqlWrapper } from 'components/shell/graphql-wrapper';
import { CustomState } from 'components/views/login-or-create-account/login-or-create-account';
import Unauthorized from 'components/views/unauthorized';
import { AuthContext, CURRENT_AVAILABLE_GROUPS, UserData } from 'providers/auth';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthRouter } from 'router';
import { isAddingAccount, setAddingAccount } from 'utils/accounts';

export const AuthWrapper: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentTokenExp, setCurrentTokenExp] = useState('');
  const [groups, setGroups] = useState<CURRENT_AVAILABLE_GROUPS[]>([]);

  const [isInternal, setIsInternal] = useState(false);
  const [user, setUser] = useState<UserData>({});

  const navigate = useNavigate();

  const getGroups = async () => {
    const { tokens } = await fetchAuthSession();
    if (!tokens?.accessToken || !tokens?.idToken) return;

    const accessToken = tokens.accessToken;
    const idToken = tokens.idToken;
    const cognitoGroups = accessToken.payload['cognito:groups'];
    const groups: string[] = Array.isArray(cognitoGroups)
      ? cognitoGroups.filter((g): g is string => typeof g === 'string')
      : [];

    const payload = idToken.payload as Record<string, string>;
    setUser({
      givenName: payload['given_name'] || '',
      familyName: payload['family_name'] || '',
      email: payload['email'] || '',
      name: payload['name'] || '',
      sub: payload['sub'] || '',
    });

    setCurrentTokenExp(String(accessToken.payload.exp || ''));

    const permissionedGroups: CURRENT_AVAILABLE_GROUPS[] = [];
    if (groups.includes(CURRENT_AVAILABLE_GROUPS.Admin)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.Admin);
      setIsInternal(true);
    }
    if (groups.includes(CURRENT_AVAILABLE_GROUPS.CustomerSuccess)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.CustomerSuccess);
      setIsInternal(true);
    }
    if (groups.includes(CURRENT_AVAILABLE_GROUPS.CustomerAdmin)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.CustomerAdmin);
      setIsInternal(false);
    } else if (groups.includes(CURRENT_AVAILABLE_GROUPS.CustomerEditor)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.CustomerEditor);
      setIsInternal(false);
    } else if (groups.includes(CURRENT_AVAILABLE_GROUPS.CustomerReadonly)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.CustomerReadonly);
      setIsInternal(false);
    } else if (groups.includes(CURRENT_AVAILABLE_GROUPS.CustomerCallCenterRep)) {
      permissionedGroups.push(CURRENT_AVAILABLE_GROUPS.CustomerCallCenterRep);
      setIsInternal(false);
    }
    setGroups(permissionedGroups);
  };

  useEffect(() => {
    if (!isLoadingAuth && isAuthenticated) {
      getGroups();
    }
  }, [isLoadingAuth, isAuthenticated]);

  const initAuth = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        const x = session.tokens.accessToken;
        setCurrentTokenExp(String(x.payload.exp || ''));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      setIsAuthenticated(false);
    }
    setIsLoadingAuth(false);
  };

  const listener: HubCallback = (data) => {
    switch (data.payload.event) {
      case 'tokenRefresh':
        getGroups();
        break;
      case 'signedIn':
        setAddingAccount(false); // also clears the add return-client pointer
        setIsAuthenticated(true);
        break;
      case 'signedOut':
        setIsAuthenticated(false);
        break;
      case 'tokenRefresh_failure':
        setIsAuthenticated(false);
        break;
      case 'customOAuthState': {
        const state = data.payload.data;
        if (typeof state === 'string') {
          try {
            const originalUrl = (JSON.parse(state) as CustomState)?.originalUrl;
            if (originalUrl) {
              navigate(originalUrl);
            }
          } catch (e) {
            console.error(`Error getting originalUrl from state: ${state}`, e);
          }
        }
        break;
      }
    }
  };

  useEffect(() => {
    initAuth();
    Hub.listen('auth', listener);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (currentTokenExp) {
        if (new Date(Number(currentTokenExp) * 1000) < new Date()) {
          setIsAuthenticated(false);
        }
      }
    }
  }, [isAuthenticated, currentTokenExp]);

  if (isLoadingAuth) {
    return null;
  }

  if (!isAuthenticated || isAddingAccount()) {
    return (
      <GraphqlWrapper>
        <AuthRouter />
      </GraphqlWrapper>
    );
  }

  if (!groups.length) {
    return <Unauthorized />;
  }

  return (
    <AuthContext.Provider
      value={{
        groups,
        isInternal,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
